/*
 * iEDM predictive search overlay — vanilla, zero-dependency.
 * Ported UI/UX from Epic Hoodie (Horizon) predictive search; reimplemented for this
 * legacy (Dawn-based) theme. Reference: /reference/epic-hoodie-search/.
 *
 * Design notes:
 * - Idle until first interaction: on load we only attach a cheap delegated trigger
 *   listener. The overlay DOM, fetch logic and default-product hydration stay dormant
 *   until the first search-icon click (buildOverlay()).
 * - Fallback: we only intercept the native search triggers AFTER this file executes.
 *   If the file fails to load, nothing is intercepted and the native Dawn <details>
 *   search modal opens as before. No dead search icon.
 * - Prices are handled as integer cents everywhere and formatted at render time from
 *   the shop money format (Shopify.money_format), so nothing money-formatted is ever
 *   persisted.
 */
(function () {
  'use strict';

  if (window.__iedmSearchInit) return; // guard against duplicate listeners / double load
  window.__iedmSearchInit = true;

  var CONFIG = readConfig();
  var TRIGGER_SELECTOR = '.header__icon--search';
  var RV_KEY = 'iedm:recentlyViewed';
  var RV_CAP = 12;
  var DEBOUNCE_MS = 250;
  var SUGGEST_LIMIT = 8;

  var overlay = null; // built lazily
  var refs = {};
  var built = false;
  var lastFocused = null;
  var debounceTimer = null;
  var hideTimer = null;
  var activeController = null;
  var queryCache = new Map(); // in-session cache: query -> products[]
  var currentQuery = '';

  /* ---------------------------------------------------------------- config */
  function readConfig() {
    var el = document.getElementById('iedm-search-config');
    var cfg = {};
    if (el) {
      try { cfg = JSON.parse(el.textContent); } catch (e) { cfg = {}; }
    }
    cfg.searchUrl = cfg.searchUrl || '/search';
    cfg.moneyFormat =
      cfg.moneyFormat ||
      (window.Shopify && window.Shopify.money_format) ||
      '${{amount}}';
    return cfg;
  }

  /* ------------------------------------------------------------ money fmt */
  // Canonical Shopify money formatter. Takes integer cents, returns a string.
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var f = format || CONFIG.moneyFormat;

    function defaultTo(num, precision, thousands, decimal) {
      precision = isNaN(precision) ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(num) || num == null) return 0;
      num = (num / 100.0).toFixed(precision);
      var parts = num.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var cts = parts[1] ? decimal + parts[1] : '';
      return dollars + cts;
    }

    switch ((f.match(placeholderRegex) || [])[1]) {
      case 'amount':
        value = defaultTo(cents, 2);
        break;
      case 'amount_no_decimals':
        value = defaultTo(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = defaultTo(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = defaultTo(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = defaultTo(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = defaultTo(cents, 0, ' ', '');
        break;
      default:
        value = defaultTo(cents, 2);
    }
    return f.replace(placeholderRegex, value);
  }

  // suggest.json returns prices as major-unit decimal strings ("59.95"); convert to cents.
  function toCents(v) {
    if (v == null || v === '') return null;
    var n = typeof v === 'number' ? v : parseFloat(v);
    if (isNaN(n)) return null;
    return Math.round(n * 100);
  }

  /* --------------------------------------------------------- image sizing */
  function sizedImage(url, width) {
    if (!url) return '';
    if (url.indexOf('//') === 0) url = 'https:' + url;
    if (/[?&]width=/.test(url)) return url; // already sized (e.g. from Liquid image_url)
    if (url.indexOf('cdn.shopify.com') === -1 && url.indexOf('/cdn/') === -1) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /* ---------------------------------------------------- recently viewed IO */
  function getRecentlyViewed() {
    try {
      var raw = localStorage.getItem(RV_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      return [];
    }
  }

  function clearRecentlyViewed() {
    try { localStorage.removeItem(RV_KEY); } catch (e) {}
  }

  /* ----------------------------------------------------------- card build */
  // Unified card renderer. product = {url, title, image, imgW, imgH, price, compare}
  // price/compare are integer cents (or null).
  function buildCard(product) {
    var a = document.createElement('a');
    a.className = 'iedm-search__card';
    a.href = product.url;

    var media = document.createElement('div');
    media.className = 'iedm-search__card-media';

    var img = document.createElement('img');
    img.className = 'iedm-search__card-img';
    img.loading = 'lazy';
    img.alt = product.title || '';
    if (product.imgW) img.width = product.imgW;
    if (product.imgH) img.height = product.imgH;
    img.src = sizedImage(product.image, 500);
    media.appendChild(img);

    var info = document.createElement('div');
    info.className = 'iedm-search__card-info';

    var title = document.createElement('div');
    title.className = 'iedm-search__card-title';
    title.textContent = product.title || '';

    var price = document.createElement('div');
    price.className = 'iedm-search__card-price';
    var onSale = product.compare != null && product.price != null && product.compare > product.price;
    if (onSale) {
      var cmp = document.createElement('s');
      cmp.className = 'iedm-search__price-compare';
      cmp.textContent = formatMoney(product.compare);
      var sale = document.createElement('span');
      sale.className = 'iedm-search__price-sale';
      sale.textContent = formatMoney(product.price);
      price.appendChild(cmp);
      price.appendChild(sale);
    } else if (product.price != null) {
      var reg = document.createElement('span');
      reg.className = 'iedm-search__price-regular';
      reg.textContent = formatMoney(product.price);
      price.appendChild(reg);
    }

    info.appendChild(title);
    info.appendChild(price);
    a.appendChild(media);
    a.appendChild(info);
    return a;
  }

  function fillGrid(gridEl, products) {
    gridEl.textContent = '';
    var frag = document.createDocumentFragment();
    for (var i = 0; i < products.length; i++) frag.appendChild(buildCard(products[i]));
    gridEl.appendChild(frag);
  }

  /* -------------------------------------------------------- overlay build */
  function buildOverlay() {
    if (built) return;
    built = true;

    overlay = document.createElement('div');
    overlay.className = 'iedm-search';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Search');
    overlay.hidden = true;
    overlay.innerHTML =
      '<div class="iedm-search__backdrop" data-iedm-close></div>' +
      '<div class="iedm-search__panel" role="document">' +
        '<div class="iedm-search__bar">' +
          '<span class="iedm-search__bar-icon" aria-hidden="true">' +
            '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"></circle><line x1="21" y1="21" x2="16.5" y2="16.5"></line></svg>' +
          '</span>' +
          '<input type="search" class="iedm-search__input" name="q" autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false" placeholder="Search" aria-label="Search" aria-controls="iedm-search-products" />' +
          '<button type="button" class="iedm-search__close" aria-label="Close search" data-iedm-close>' +
            '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>' +
          '</button>' +
        '</div>' +
        '<div class="iedm-search__body">' +
          // Products first (directly under the search bar), Recently viewed below — all devices, all states.
          // CLEAR sits top-right on the Products heading row; it still clears Recently viewed only.
          '<section class="iedm-search__section iedm-search__products">' +
            '<div class="iedm-search__section-head">' +
              '<h2 class="iedm-search__heading">Products</h2>' +
              '<button type="button" class="iedm-search__clear" hidden>CLEAR</button>' +
            '</div>' +
            '<div class="iedm-search__grid" id="iedm-search-products" data-iedm-products aria-live="polite"></div>' +
            '<p class="iedm-search__empty" data-iedm-noresults hidden>No products found.</p>' +
          '</section>' +
          '<section class="iedm-search__section iedm-search__recent" hidden>' +
            '<h2 class="iedm-search__heading">Recently viewed</h2>' +
            '<div class="iedm-search__grid" data-iedm-recent></div>' +
          '</section>' +
        '</div>' +
      '</div>';

    document.body.appendChild(overlay);

    refs.panel = overlay.querySelector('.iedm-search__panel');
    refs.backdrop = overlay.querySelector('.iedm-search__backdrop');
    refs.input = overlay.querySelector('.iedm-search__input');
    refs.recentSection = overlay.querySelector('.iedm-search__recent');
    refs.recentGrid = overlay.querySelector('[data-iedm-recent]');
    refs.productsGrid = overlay.querySelector('[data-iedm-products]');
    refs.noResults = overlay.querySelector('[data-iedm-noresults]');
    refs.clear = overlay.querySelector('.iedm-search__clear');

    // events
    // Close on backdrop click: listener lives ON the backdrop element itself. The panel is a
    // SIBLING of the backdrop (not a descendant), so clicks inside the panel never reach here.
    refs.backdrop.addEventListener('click', close);
    // Close on the X button (delegated on the panel container).
    refs.panel.addEventListener('click', function (e) {
      if (e.target.closest('.iedm-search__close')) close();
    });
    refs.clear.addEventListener('click', function () {
      clearRecentlyViewed();
      renderRecentlyViewed();
    });
    refs.input.addEventListener('input', onInput);
    refs.input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        var q = refs.input.value.trim();
        if (q) window.location.href = CONFIG.searchUrl + '?q=' + encodeURIComponent(q);
      }
    });
    overlay.addEventListener('keydown', onKeydown);
  }

  /* ------------------------------------------------------------ defaults */
  // Server-rendered best-sellers live in a <template>; parse once into product objects.
  var defaultProducts = null;
  function getDefaultProducts() {
    if (defaultProducts) return defaultProducts;
    defaultProducts = [];
    var tpl = document.getElementById('iedm-search-default-products');
    if (tpl && tpl.content) {
      var nodes = tpl.content.querySelectorAll('[data-product]');
      for (var i = 0; i < nodes.length; i++) {
        var n = nodes[i];
        defaultProducts.push({
          url: n.getAttribute('data-url'),
          title: n.getAttribute('data-title'),
          image: n.getAttribute('data-image'),
          imgW: parseInt(n.getAttribute('data-img-w'), 10) || null,
          imgH: parseInt(n.getAttribute('data-img-h'), 10) || null,
          price: n.getAttribute('data-price') !== '' ? parseInt(n.getAttribute('data-price'), 10) : null,
          compare: n.getAttribute('data-compare') !== '' ? parseInt(n.getAttribute('data-compare'), 10) : null
        });
      }
    }
    return defaultProducts;
  }

  function renderRecentlyViewed() {
    var items = getRecentlyViewed().slice(0, RV_CAP).map(function (it) {
      return {
        url: '/products/' + it.handle,
        title: it.title,
        image: it.image,
        imgW: it.w || null,
        imgH: it.h || null,
        price: typeof it.price === 'number' ? it.price : null,
        compare: typeof it.compare === 'number' ? it.compare : null
      };
    });
    if (items.length) {
      fillGrid(refs.recentGrid, items);
      refs.recentSection.hidden = false;
      refs.clear.hidden = false; // CLEAR (on the Products row) only when there's something to clear
    } else {
      refs.recentGrid.textContent = '';
      refs.recentSection.hidden = true;
      refs.clear.hidden = true;
    }
  }

  function showDefaults() {
    refs.noResults.hidden = true;
    fillGrid(refs.productsGrid, getDefaultProducts());
  }

  /* -------------------------------------------------------------- search */
  function onInput() {
    var q = refs.input.value.trim();
    currentQuery = q;
    if (debounceTimer) clearTimeout(debounceTimer);
    if (!q) {
      if (activeController) { activeController.abort(); activeController = null; }
      showDefaults();
      return;
    }
    debounceTimer = setTimeout(function () { runSearch(q); }, DEBOUNCE_MS);
  }

  function runSearch(q) {
    if (queryCache.has(q)) {
      if (q === currentQuery) renderResults(queryCache.get(q));
      return;
    }
    if (activeController) activeController.abort();
    activeController = new AbortController();

    var url =
      CONFIG.searchUrl +
      '/suggest.json?q=' + encodeURIComponent(q) +
      '&resources[type]=product&resources[limit]=' + SUGGEST_LIMIT +
      '&resources[options][unavailable_products]=last';

    fetch(url, { signal: activeController.signal, headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var raw =
          (data.resources && data.resources.results && data.resources.results.products) || [];
        var products = raw.map(function (p) {
          var fi = p.featured_image || null;
          return {
            url: p.url,
            title: p.title,
            image: (fi && fi.url) || p.image || '',
            imgW: fi && fi.width ? fi.width : null,
            imgH: fi && fi.height ? fi.height : null,
            price: toCents(p.price),
            compare: toCents(p.compare_at_price != null ? p.compare_at_price : p.compare_at_price_max)
          };
        });
        queryCache.set(q, products);
        if (q === currentQuery) renderResults(products);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        // network failure: leave existing content, surface nothing noisy
      });
  }

  function renderResults(products) {
    if (!products.length) {
      refs.productsGrid.textContent = '';
      refs.noResults.hidden = false;
      return;
    }
    refs.noResults.hidden = true;
    fillGrid(refs.productsGrid, products);
  }

  /* --------------------------------------------------------- open / close */
  function open() {
    buildOverlay();
    if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
    lastFocused = document.activeElement;
    renderRecentlyViewed();
    if (currentQuery && queryCache.has(currentQuery)) {
      renderResults(queryCache.get(currentQuery));
    } else if (!refs.input.value.trim()) {
      showDefaults();
    }
    overlay.hidden = false;
    document.documentElement.classList.add('iedm-search-open');
    document.body.style.overflow = 'hidden';
    // next frame -> transition in + focus
    requestAnimationFrame(function () {
      overlay.classList.add('is-open');
      refs.input.focus();
    });
  }

  function close() {
    if (!overlay || overlay.hidden) return;
    overlay.classList.remove('is-open');
    document.documentElement.classList.remove('iedm-search-open');
    document.body.style.overflow = '';
    if (activeController) { activeController.abort(); activeController = null; }
    // allow CSS transition to play; guard against a reopen during the window
    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = setTimeout(function () {
      if (overlay && !overlay.classList.contains('is-open')) overlay.hidden = true;
      hideTimer = null;
    }, 200);
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
  }

  function onKeydown(e) {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key === 'Tab') trapFocus(e);
  }

  function trapFocus(e) {
    var focusables = overlay.querySelectorAll(
      'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
    );
    var list = Array.prototype.filter.call(focusables, function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
    if (!list.length) return;
    var first = list[0];
    var last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  /* --------------------------------------------------- trigger interception */
  // Runs only because this file loaded -> native modal is the automatic fallback.
  function onTriggerActivate(e) {
    var trigger = e.target.closest(TRIGGER_SELECTOR);
    if (!trigger) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    open();
  }

  document.addEventListener('click', onTriggerActivate, true);
  document.addEventListener(
    'keydown',
    function (e) {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      var trigger = e.target.closest && e.target.closest(TRIGGER_SELECTOR);
      if (!trigger) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      open();
    },
    true
  );

  // ⌘K / Ctrl+K to open (parity with Epic Hoodie)
  document.addEventListener('keydown', function (e) {
    if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
      e.preventDefault();
      if (overlay && !overlay.hidden) { close(); } else { open(); }
    }
  });
})();
